/**
 * Step 3, statuses (2.J deliverable 14): a canon type, one of its select fields (or a global
 * one), and a table mapping each option to a canon status and a play state. Options left at "No
 * change" are not sent.
 */
import type { BootstrapStatusV1, DocTypeV1, FieldDefV1 } from '@codex/contracts';
import { Button, CellMenu, type CellMenuOption } from '@codex/ui';
import { statusMapOptions } from '../../import/model/mapping-draft.js';
import { STATUS_KIND, statusApi, useBatches, useTypes } from '../data.js';
import { useDraft } from '../drafts.js';
import { batchRows } from '../model/step-undo.js';
import { StepPanel } from '../step-panel.js';
import { AppliedList, undoBatch } from '../step-undo.js';
import { useStep } from '../use-step.js';

type Canon = (typeof statusMapOptions)[number];
type Play = 'played' | 'skipped';
type Choice<V extends string> = V | 'none';
interface Mapped {
  readonly canon: Choice<Canon>;
  readonly play: Choice<Play>;
}

const CANON_WORDS: Readonly<Record<Canon, string>> = {
  draft: 'Draft',
  canon: 'Canon',
  retired: 'Retired',
};
const CANON_OPTIONS: readonly CellMenuOption<Choice<Canon>>[] = [
  { value: 'none', label: 'No change' },
  ...statusMapOptions.map((value) => ({ value, label: CANON_WORDS[value] })),
];
const NOT_PLAYABLE = "This type can't be played.";

const playOptions = (playable: boolean): readonly CellMenuOption<Choice<Play>>[] => [
  { value: 'none', label: 'No change' },
  {
    value: 'played',
    label: 'Played',
    disabled: !playable,
    ...(playable ? {} : { disabledReason: NOT_PLAYABLE }),
  },
  {
    value: 'skipped',
    label: 'Skipped',
    disabled: !playable,
    ...(playable ? {} : { disabledReason: NOT_PLAYABLE }),
  },
];

type SelectField = Extract<FieldDefV1, { kind: 'select' | 'multi_select' }>;

/** The select fields a type reads: its own and the global ones. */
export function selectFields(
  type: DocTypeV1 | undefined,
  all: readonly DocTypeV1[],
): SelectField[] {
  if (type === undefined) return [];
  const globals = all.flatMap((t) => t.fields).filter((f) => f.docTypeId === null);
  const fields = [
    ...type.fields,
    ...globals.filter((g) => !type.fields.some((f) => f.id === g.id)),
  ];
  return fields.filter((f): f is SelectField => f.kind === 'select' && !f.archived);
}

export function statusRequest(
  typeKey: string,
  fieldKey: string,
  options: readonly string[],
  map: Readonly<Record<string, Mapped>>,
): BootstrapStatusV1 | null {
  const entries = options.flatMap((option) => {
    const m = map[option];
    if (m === undefined || (m.canon === 'none' && m.play === 'none')) return [];
    return [
      {
        option,
        canonStatus: m.canon === 'none' ? null : m.canon,
        playState: m.play === 'none' ? null : m.play,
      },
    ];
  });
  return entries.length === 0 ? null : { typeKey, fieldKey, map: entries };
}

export function StatusStep() {
  const step = useStep(statusApi);
  const types = useTypes();
  const batches = useBatches(STATUS_KIND);
  const [typeKey, setTypeKey] = useDraft<string | null>('status:type', null);
  const [fieldKey, setFieldKey] = useDraft<string | null>('status:field', null);
  const [map, setMap] = useDraft<Readonly<Record<string, Mapped>>>('status:map', {});

  const all = types.data ?? [];
  const canonTypes = all.filter((t) => t.library === 'canon' && !t.archived);
  const type = canonTypes.find((t) => t.key === typeKey);
  const fields = selectFields(type, all);
  const field = fields.find((f) => f.key === fieldKey);
  const options = field?.config.options ?? [];
  const playable = type?.capabilities.includes('playable') === true;
  const request =
    type === undefined || field === undefined
      ? null
      : statusRequest(type.key, field.key, options, map);

  const set = (option: string, patch: Partial<Mapped>) => {
    setMap({ ...map, [option]: { canon: 'none', play: 'none', ...map[option], ...patch } });
    step.edit();
  };
  const reset = () => {
    setTypeKey(null);
    setFieldKey(null);
    setMap({});
    step.edit();
  };

  return (
    <form
      className="cx-bootstrap__step"
      onSubmit={(event) => {
        event.preventDefault();
        if (request !== null) void step.preview(request);
      }}
    >
      <p className="cx-bootstrap__muted">
        Choose a type and a select field its pages carry, then say what each option means for a
        page's status and whether it was played.
      </p>
      <div className="cx-bootstrap__fields">
        <CellMenu
          label="Type"
          placeholder="Choose a type"
          options={canonTypes.map((t) => ({ value: t.key, label: t.name }))}
          value={typeKey}
          onChange={(value) => {
            setTypeKey(value);
            setFieldKey(null);
            setMap({});
            step.edit();
          }}
        />
        <CellMenu
          label="Field"
          placeholder={type === undefined ? 'Choose a type first' : 'Choose a field'}
          options={fields.map((f) => ({ value: f.key, label: f.label }))}
          value={fieldKey}
          onChange={(value) => {
            setFieldKey(value);
            setMap({});
            step.edit();
          }}
        />
        <Button onClick={reset}>Map another field</Button>
      </div>
      {field !== undefined && (
        <table data-testid="bootstrap.status.map" className="cx-bootstrap__table">
          <caption className="cx-visually-hidden">What each option means</caption>
          <thead>
            <tr>
              <th scope="col">Option</th>
              <th scope="col">Canon status</th>
              <th scope="col">Play state</th>
            </tr>
          </thead>
          <tbody>
            {options.map((option) => (
              <tr key={option}>
                <th scope="row">{option}</th>
                <td>
                  <CellMenu
                    label={`Status for ${option}`}
                    options={CANON_OPTIONS}
                    value={map[option]?.canon ?? 'none'}
                    onChange={(canon) => set(option, { canon })}
                  />
                </td>
                <td>
                  <CellMenu
                    label={`Play state for ${option}`}
                    options={playOptions(playable)}
                    value={map[option]?.play ?? 'none'}
                    onChange={(play) => set(option, { play })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <StepPanel
        state={step.state}
        canApply={step.canApply}
        previewDisabled={request === null}
        previewNote={request === null ? 'Map at least one option to preview.' : undefined}
        onApply={() => void step.apply()}
      >
        <AppliedList
          rows={batchRows(batches.data ?? [], STATUS_KIND)}
          undo={(row) => undoBatch(row.id)}
          onUndone={() => void step.refresh()}
        />
      </StepPanel>
    </form>
  );
}
